import React, { useEffect, useState, useCallback, useRef } from 'react';
import type { ChatMessage } from '@/types/chat';
import type { TaskChatType, AssignableUser } from '@/types/tasks';
import { getTaskChat } from '@/services/api';
import { chatMessagesApi, chatMembersApi } from '@/services/chatApi';
import UserAvatar from '@/components/UserAvatar';

interface TaskChatPanelProps {
  taskId: string;
  chatType: TaskChatType;
  assignableUsers?: AssignableUser[];
}

const POLL_INTERVAL = 8000;

const TaskChatPanel: React.FC<TaskChatPanelProps> = ({ taskId, chatType, assignableUsers = [] }) => {
  const [channelId, setChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);

  // Get or create the chat channel
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const { channel_id } = await getTaskChat(taskId, chatType);
        if (!cancelled) {
          setChannelId(channel_id);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to initialize chat');
          setLoading(false);
        }
      }
    };
    init();
    return () => { cancelled = true; };
  }, [taskId, chatType]);

  // Load messages when channel is ready
  const loadMessages = useCallback(async () => {
    if (!channelId) return;
    try {
      const msgs = await chatMessagesApi.list(channelId, undefined, 100);
      // Messages come newest-first from API, reverse for display
      const sorted = [...msgs].reverse();
      setMessages(sorted);
      if (sorted.length > 0) {
        lastMessageIdRef.current = sorted[sorted.length - 1].id;
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    if (channelId) {
      loadMessages();
      // Poll for new messages
      pollRef.current = setInterval(loadMessages, POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [channelId, loadMessages]);

  const handleSend = async () => {
    if (!channelId || !newMessage.trim() || sending) return;

    const content = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const sent = await chatMessagesApi.send(channelId, { content });
      setMessages(prev => [...prev, sent]);
    } catch (err) {
      console.error('Failed to send message:', err);
      setNewMessage(content); // Restore on failure
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInviteUser = async (userId: number) => {
    if (!channelId) return;
    try {
      await chatMembersApi.add(channelId, userId);
      setShowInvite(false);
    } catch (err) {
      console.error('Failed to invite user:', err);
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-red-600">
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
        <span className="ml-2 text-sm text-gray-500">Loading chat...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-80 border border-gray-200 rounded-lg bg-white">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            No messages yet. Start the conversation.
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5">
              <UserAvatar
                user={{
                  id: msg.user_id,
                  full_name: msg.full_name ?? undefined,
                  username: msg.username ?? undefined,
                  avatar_url: msg.avatar_url,
                }}
                size="sm"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-medium text-gray-900">
                  {msg.full_name || msg.username || 'Unknown'}
                </span>
                <span className="text-xs text-gray-400">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">{msg.content}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-gray-200 px-3 py-2">
        <div className="flex items-end gap-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Enter to send)"
            rows={1}
            className="flex-1 resize-none rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <div className="flex items-center gap-1">
            {assignableUsers.length > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowInvite(!showInvite)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Invite user"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </button>
                {showInvite && (
                  <div className="absolute bottom-full right-0 mb-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10">
                    {assignableUsers.map((u) => (
                      <button
                        key={u.user_id}
                        onClick={() => handleInviteUser(u.user_id)}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <UserAvatar
                          user={{
                            id: u.user_id,
                            full_name: u.full_name,
                            username: u.username,
                            avatar_url: u.avatar_url,
                          }}
                          size="sm"
                        />
                        <span className="truncate">{u.full_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              title="Send"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskChatPanel;
